import {
    clampDescription,
    clampName,
    clampRoleLabel,
    clampTitle,
    MAX_NODES,
    MAX_ROLES
} from "./orgStructureSchema";
import {
    CompanyOrgStructureDocument,
    EMPTY_ORG_STRUCTURE_DOCUMENT,
    OrgNode,
    OrgNodeKind,
    OrgPerson,
    OrgRole
} from "./orgStructureTypes";

export function newId(prefix: string): string {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return `${prefix}-${crypto.randomUUID()}`;
    }
    return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function cloneDocument(document: CompanyOrgStructureDocument): CompanyOrgStructureDocument {
    return JSON.parse(JSON.stringify(document)) as CompanyOrgStructureDocument;
}

export function documentsEqual(
    left: CompanyOrgStructureDocument,
    right: CompanyOrgStructureDocument
): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
}

export function sortRolesForDisplay(roles: OrgRole[], locale: string): OrgRole[] {
    return [...roles].sort((a, b) =>
        a.label.localeCompare(b.label, locale, { sensitivity: "base" })
    );
}

function findNode(document: CompanyOrgStructureDocument, nodeId: string): OrgNode | undefined {
    return document.nodes.find(node => node.id === nodeId);
}

function collectSubtreeIds(document: CompanyOrgStructureDocument, rootId: string): Set<string> {
    const ids = new Set<string>([rootId]);
    let changed = true;
    while (changed) {
        changed = false;
        for (const node of document.nodes) {
            if (node.parent_id !== null && ids.has(node.parent_id) && !ids.has(node.id)) {
                ids.add(node.id);
                changed = true;
            }
        }
    }
    return ids;
}

export function isRoleReferenced(document: CompanyOrgStructureDocument, roleId: string): boolean {
    return document.nodes.some(node => node.people.some(person => person.role_id === roleId));
}

export function addRole(
    document: CompanyOrgStructureDocument,
    label: string
): { document: CompanyOrgStructureDocument; role: OrgRole } {
    const draft = cloneDocument(document);
    if (draft.roles.length >= MAX_ROLES) {
        throw new Error(`Maximum ${MAX_ROLES} roles allowed`);
    }
    const role: OrgRole = { id: newId("role"), label: clampRoleLabel(label.trim()) };
    draft.roles.push(role);
    return { document: draft, role };
}

export function updateRole(
    document: CompanyOrgStructureDocument,
    roleId: string,
    label: string
): CompanyOrgStructureDocument {
    const draft = cloneDocument(document);
    const role = draft.roles.find(item => item.id === roleId);
    if (!role) {
        throw new Error(`Role not found: ${roleId}`);
    }
    role.label = clampRoleLabel(label.trim());
    return draft;
}

export function removeRole(document: CompanyOrgStructureDocument, roleId: string): CompanyOrgStructureDocument {
    if (isRoleReferenced(document, roleId)) {
        throw new Error(`Role is still referenced: ${roleId}`);
    }
    const draft = cloneDocument(document);
    draft.roles = draft.roles.filter(role => role.id !== roleId);
    return draft;
}

export function addNode(
    document: CompanyOrgStructureDocument,
    kind: OrgNodeKind,
    parentId?: string | null
): { document: CompanyOrgStructureDocument; node: OrgNode } {
    const draft = cloneDocument(document);
    if (draft.nodes.length >= MAX_NODES) {
        throw new Error(`Maximum ${MAX_NODES} nodes allowed`);
    }
    if (parentId) {
        if (!findNode(draft, parentId)) {
            throw new Error(`Parent node not found: ${parentId}`);
        }
    }
    const node: OrgNode = {
        id: newId("node"),
        kind,
        parent_id: parentId ?? null,
        title: "",
        description: "",
        people: []
    };
    draft.nodes.push(node);
    return { document: draft, node };
}

export function updateNode(
    document: CompanyOrgStructureDocument,
    nodeId: string,
    fields: { title?: string; description?: string }
): CompanyOrgStructureDocument {
    const draft = cloneDocument(document);
    const node = findNode(draft, nodeId);
    if (!node) {
        throw new Error(`Node not found: ${nodeId}`);
    }
    if (fields.title !== undefined) {
        node.title = clampTitle(fields.title.trim());
    }
    if (fields.description !== undefined) {
        node.description = clampDescription(fields.description.trim());
    }
    return draft;
}

export function addPerson(
    document: CompanyOrgStructureDocument,
    nodeId: string,
    fields: { name: string; role_id: string }
): { document: CompanyOrgStructureDocument; person: OrgPerson } {
    const draft = cloneDocument(document);
    const node = findNode(draft, nodeId);
    if (!node) {
        throw new Error(`Node not found: ${nodeId}`);
    }
    if (!draft.roles.some(role => role.id === fields.role_id)) {
        throw new Error(`Role not found: ${fields.role_id}`);
    }
    const person: OrgPerson = {
        id: newId("person"),
        name: clampName(fields.name.trim()),
        role_id: fields.role_id
    };
    node.people.push(person);
    return { document: draft, person };
}

export function updatePerson(
    document: CompanyOrgStructureDocument,
    nodeId: string,
    personId: string,
    fields: { name?: string; role_id?: string }
): CompanyOrgStructureDocument {
    const draft = cloneDocument(document);
    const node = findNode(draft, nodeId);
    if (!node) {
        throw new Error(`Node not found: ${nodeId}`);
    }
    const person = node.people.find(item => item.id === personId);
    if (!person) {
        throw new Error(`Person not found: ${personId}`);
    }
    if (fields.name !== undefined) {
        person.name = clampName(fields.name.trim());
    }
    if (fields.role_id !== undefined) {
        if (!draft.roles.some(role => role.id === fields.role_id)) {
            throw new Error(`Role not found: ${fields.role_id}`);
        }
        person.role_id = fields.role_id;
    }
    return draft;
}

export function removePerson(
    document: CompanyOrgStructureDocument,
    nodeId: string,
    personId: string
): CompanyOrgStructureDocument {
    const draft = cloneDocument(document);
    const node = findNode(draft, nodeId);
    if (!node) {
        throw new Error(`Node not found: ${nodeId}`);
    }
    node.people = node.people.filter(person => person.id !== personId);
    return draft;
}

export function deleteNode(document: CompanyOrgStructureDocument, nodeId: string): CompanyOrgStructureDocument {
    const draft = cloneDocument(document);
    if (!findNode(draft, nodeId)) {
        throw new Error(`Node not found: ${nodeId}`);
    }
    const toRemove = collectSubtreeIds(draft, nodeId);
    draft.nodes = draft.nodes.filter(node => !toRemove.has(node.id));
    return draft;
}

export function countSubtreeNodes(document: CompanyOrgStructureDocument, nodeId: string): number {
    return collectSubtreeIds(document, nodeId).size;
}

export function normalizeDocument(data: unknown): CompanyOrgStructureDocument {
    if (!data || typeof data !== "object") {
        return cloneDocument(EMPTY_ORG_STRUCTURE_DOCUMENT);
    }
    const raw = data as Partial<CompanyOrgStructureDocument>;
    return {
        schema_version: typeof raw.schema_version === "number" ? raw.schema_version : 1,
        roles: Array.isArray(raw.roles) ? raw.roles : [],
        nodes: Array.isArray(raw.nodes) ? raw.nodes : [],
        updated_at: typeof raw.updated_at === "number" ? raw.updated_at : undefined,
        updated_by: typeof raw.updated_by === "string" ? raw.updated_by : undefined
    };
}
